import filterNodeTypes from '@/utils/filterNodeTypes';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { resourcesActions } from './resourcesStore';
import { settingsActions } from './settingsStore';
import { NODE_TYPES, SYSTEM_NAMES_TO_TYPES, SYSTEM_STATUS } from '@/consts';
import { getRegistry, getSubpathwayRegistry } from '@/api/registry';

const initialValues = {
  searchParams: {},
  activeCategory: null,
  highlightedCustomId: null,
  nodeTypesByCategory: {},
  productList: [],
};

const productList = [
  'Select Product',
  'Ammonia',
  'CO₂',
  'Coal',
  'Electricity',
  'Hydrogen',
  'Liquid Hydrogen',
  'Liquid Natural Gas',
  'Natural Gas',
  'Nitrogen',
];

const useRegistryStore = create()(immer(() => initialValues));

const { setState: set, getState: get } = useRegistryStore;

const HIGHLIGHT_MS = 4000;
let highlightTimeout;

// actions
export const registryActions = {
  init: (projectId, registry = [], context = {}) => {
    const nodeTypes = registry ?? [];
    const activeCategory = nodeTypes?.[0]?.categories?.[0];
    const allNodes = [];
    const systemNodeTypes = [];
    const nodeTypesByCategory = nodeTypes.reduce((acc, nodeType) => {
      const { categories, search_terms, label } = nodeType;
      const searchIndex = [label, ...search_terms].join(' ');

      nodeType.searchIndex = searchIndex;

      categories?.forEach(category => {
        if (category) {
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(nodeType);
        }
      });

      if (categories?.includes('Systems')) {
        systemNodeTypes.push(nodeType);
      } else {
        allNodes.push(nodeType);
      }
      return acc;
    }, {});

    nodeTypesByCategory.All = allNodes;

    set({ projectId, registryContext: context, nodeTypesByCategory, systemNodeTypes, productList, activeCategory });
  },

  refresh: async () => {
    const { projectId, registryContext } = get();
    const { caseId, nodeId } = registryContext ?? {};
    const registryRequest = caseId && nodeId ? getSubpathwayRegistry(caseId, nodeId) : getRegistry(projectId);
    const {
      data: { node_types: nodeTypes },
    } = await registryRequest;
    registryActions.init(projectId, nodeTypes, registryContext);
    registryActions.loadSystems();
  },

  updateSearch: searchParams => set({ searchParams }),
  setActiveCategory: activeCategory => set({ activeCategory }),

  // Point the palette at a just-created custom module: open it on "Custom"
  // (the backend puts every custom module there) and highlight the module
  // for a few seconds. Does nothing when it isn't in the loaded registry.
  highlightCustomModule: customId => {
    if (!get().nodeTypesByCategory.Custom?.some(n => n.custom_id === customId)) return;
    set({ activeCategory: 'Custom', searchParams: {}, highlightedCustomId: customId });
    settingsActions.openPalette();
    clearTimeout(highlightTimeout);
    highlightTimeout = setTimeout(() => set({ highlightedCustomId: null }), HIGHLIGHT_MS);
  },

  loadSystems: () => {
    const { systemNodeTypes = [], nodeTypesByCategory } = get();
    const systems = resourcesActions.getSystems()?.filter(system => system.status === SYSTEM_STATUS.succeeded);
    const systemNodes = [];

    systemNodeTypes.forEach(nodeType => {
      const systemType = SYSTEM_NAMES_TO_TYPES[nodeType.name];
      systems.forEach(system => {
        if (system.system_type === systemType) {
          systemNodes.push({
            ...nodeType,
            label: system.name,
            type: NODE_TYPES.system,
            data: {
              system,
            },
          });
        }
      });
    });

    set({ nodeTypesByCategory: { ...nodeTypesByCategory, Systems: systemNodes } });
  },
  clear: () => set({ searchParams: {}, highlightedCustomId: null }),
};

export const useProductList = () => useRegistryStore(store => store.productList);
export const useCategories = () =>
  useRegistryStore(store => {
    return Object.entries(store.nodeTypesByCategory)
      .filter(([_, value]) => Array.isArray(value) && value.length > 0)
      .map(([key]) => key);
  });
export const useActiveNodeTypes = () =>
  useRegistryStore(store => {
    const { searchParams, nodeTypesByCategory, activeCategory } = store;
    const { term, product } = searchParams;
    let activeNodeTypes = filterNodeTypes(term, activeCategory, product, nodeTypesByCategory);

    return activeNodeTypes.sort((a, b) => {
      if (term) {
        const termLower = term.toLowerCase();
        const aLabelMatch = a.label.toLowerCase().includes(termLower);
        const bLabelMatch = b.label.toLowerCase().includes(termLower);

        if (aLabelMatch !== bLabelMatch) {
          return aLabelMatch ? -1 : 1;
        }
      }

      return a.label.localeCompare(b.label);
    });
  });
export const useActiveCategory = () => useRegistryStore(store => store.activeCategory);
export const useHighlightedCustomId = () => useRegistryStore(store => store.highlightedCustomId);

export default useRegistryStore;
